import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { extractErrorMessage } from '@/lib/api';
import type { Investor, InvestorInput } from '@/lib/types';
import { LedgerView } from '@/components/ledgers/LedgerView';
import { InvestorRateSheet } from '@/components/investors/InvestorRateSheet';
import { InvestorVehiclesSection } from '@/components/investors/InvestorVehiclesSection';

interface InvestorFormProps {
  initial?: Investor | null;
  onSubmit: (input: InvestorInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const blank: InvestorInput = {
  fullNameEn: '',
  fullNameAr: '',
  fullNameUr: '',
  nationalId: '',
  phone: '',
  email: '',
  bankName: '',
  iban: '',
  accountHolder: '',
  swiftCode: '',
  joinedDate: '',
  status: 'active',
  notes: '',
  paymentTermsDays: 30, // PR-MAY16-INVESTOR-RATES-V2 — net-30 default
};

export function InvestorForm({ initial, onSubmit, onCancel, submitLabel }: InvestorFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<InvestorInput>(blank);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setForm({
        fullNameEn: initial.fullNameEn,
        fullNameAr: initial.fullNameAr ?? '',
        fullNameUr: initial.fullNameUr ?? '',
        nationalId: initial.nationalId ?? '',
        phone: initial.phone,
        email: initial.email ?? '',
        bankName: initial.bankName ?? '',
        iban: initial.iban ?? '',
        accountHolder: initial.accountHolder ?? '',
        swiftCode: initial.swiftCode ?? '',
        joinedDate: initial.joinedDate ? initial.joinedDate.slice(0, 10) : '',
        status: initial.status,
        notes: initial.notes ?? '',
        paymentTermsDays: initial.paymentTermsDays ?? 30,
      });
    } else {
      setForm(blank);
    }
  }, [initial]);

  const update = <K extends keyof InvestorInput>(key: K, value: InvestorInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic client-side validation
    if (!form.fullNameEn?.trim()) {
      setError(t('investors.errors.nameRequired'));
      return;
    }
    if (!form.phone?.trim()) {
      setError(t('investors.errors.phoneRequired'));
      return;
    }

    // Clean payload — drop empty strings so API sees them as nulls
    const payload: InvestorInput = {
      fullNameEn: form.fullNameEn.trim(),
      phone: form.phone.trim(),
      status: form.status,
    };
    if (form.fullNameAr?.trim()) payload.fullNameAr = form.fullNameAr.trim();
    if (form.fullNameUr?.trim()) payload.fullNameUr = form.fullNameUr.trim();
    if (form.nationalId?.trim()) payload.nationalId = form.nationalId.trim();
    if (form.email?.trim()) payload.email = form.email.trim();
    if (form.bankName?.trim()) payload.bankName = form.bankName.trim();
    if (form.iban?.trim()) payload.iban = form.iban.trim();
    if (form.accountHolder?.trim()) payload.accountHolder = form.accountHolder.trim();
    if (form.swiftCode?.trim()) payload.swiftCode = form.swiftCode.trim();
    if (form.joinedDate?.trim()) payload.joinedDate = form.joinedDate.trim();
    if (form.notes?.trim()) payload.notes = form.notes.trim();
    if (form.paymentTermsDays !== undefined && form.paymentTermsDays !== null) {
      payload.paymentTermsDays = Number(form.paymentTermsDays);
    }

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // PR-MAY16-INVESTOR-RATES-V2 item 3 — five-section layout
    // (Identity / Payment Terms / Rate Sheet / Vehicles / Ledger). All
    // sections live inside the single scrollable container above the
    // sticky save bar. Yesterday's 3-section layout collapsed the rate
    // model into one panel — V2 splits the per-investor rate sheet from
    // the per-vehicle override view.
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto p-6 space-y-8" data-investor-form-scroll>
        {/* SECTION 1 — Identity (names, contact, notes) */}
        <section data-section="identity" className="space-y-6">
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('investors.section.identity')}
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t('investors.field.fullNameEn')} required>
              <Input value={form.fullNameEn ?? ''} onChange={(e) => update('fullNameEn', e.target.value)} required />
            </Field>
            <Field label={t('investors.field.phone')} required>
              <Input dir="ltr" value={form.phone ?? ''} onChange={(e) => update('phone', e.target.value)} placeholder="+966..." required />
            </Field>
            <Field label={t('investors.field.fullNameAr')}>
              <Input dir="rtl" value={form.fullNameAr ?? ''} onChange={(e) => update('fullNameAr', e.target.value)} className="font-arabic" />
            </Field>
            <Field label={t('investors.field.fullNameUr')}>
              <Input dir="rtl" value={form.fullNameUr ?? ''} onChange={(e) => update('fullNameUr', e.target.value)} className="font-urdu" />
            </Field>
            <Field label={t('investors.field.nationalId')}>
              <Input value={form.nationalId ?? ''} onChange={(e) => update('nationalId', e.target.value)} />
            </Field>
            <Field label={t('investors.field.email')}>
              <Input type="email" dir="ltr" value={form.email ?? ''} onChange={(e) => update('email', e.target.value)} />
            </Field>
            <Field label={t('investors.field.joinedDate')}>
              <Input type="date" value={form.joinedDate ?? ''} onChange={(e) => update('joinedDate', e.target.value)} />
            </Field>
            <Field label={t('investors.field.status')}>
              <select
                value={form.status ?? 'active'}
                onChange={(e) => update('status', e.target.value as 'active' | 'inactive')}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="active">{t('investors.status.active')}</option>
                <option value="inactive">{t('investors.status.inactive')}</option>
              </select>
            </Field>
          </div>
        </fieldset>

        {/* Notes */}
        <Field label={t('investors.field.notes')}>
          <textarea
            value={form.notes ?? ''}
            onChange={(e) => update('notes', e.target.value)}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </Field>

        {error && (
          <p className="text-sm text-destructive p-3 rounded bg-destructive/10 border border-destructive/30" role="alert">
            {error}
          </p>
        )}
        </section>

        {/* SECTION 2 — Payment Terms (banking + payout window). The
            payment_terms_days field defaults to 30 (net-30) and matches
            every contract Ayan has signed to date. */}
        <section data-section="payment-terms" className="space-y-3 border-t border-border pt-6">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('investors.section.paymentTerms')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t('investors.field.bankName')}>
              <Input value={form.bankName ?? ''} onChange={(e) => update('bankName', e.target.value)} />
            </Field>
            <Field label={t('investors.field.swiftCode')}>
              <Input dir="ltr" value={form.swiftCode ?? ''} onChange={(e) => update('swiftCode', e.target.value)} />
            </Field>
            <Field label={t('investors.field.iban')} className="md:col-span-2">
              <Input dir="ltr" value={form.iban ?? ''} onChange={(e) => update('iban', e.target.value)} className="font-mono" placeholder="SA00 0000 0000 0000 0000 0000" />
            </Field>
            <Field label={t('investors.field.accountHolder')} className="md:col-span-2">
              <Input value={form.accountHolder ?? ''} onChange={(e) => update('accountHolder', e.target.value)} />
            </Field>
            <Field label={t('investors.field.paymentTermsDays')}>
              <Input
                type="number" min={0} max={365} step={1} dir="ltr"
                value={form.paymentTermsDays ?? 30}
                onChange={(e) => update('paymentTermsDays', e.target.value === '' ? 30 : Number(e.target.value))}
                className="tabular-nums"
                data-investor-payment-terms-days
              />
            </Field>
          </div>
        </section>

        {/* SECTION 3 — Rate Sheet (per-investor defaults). One row per
            (contract, effective_from). Every vehicle inherits unless
            overridden in section 4. */}
        {initial?.id && (
          <section data-section="rate-sheet" className="space-y-3 border-t border-border pt-6">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {t('investors.section.rateSheet')}
            </h3>
            <InvestorRateSheet investorId={initial.id} />
          </section>
        )}

        {/* SECTION 4 — Vehicles. Lists the investor's trucks + a column
            showing how many overrides each has against the default. */}
        {initial?.id && (
          <section data-section="vehicles" className="space-y-3 border-t border-border pt-6">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {t('investors.section.vehicles')}
            </h3>
            <InvestorVehiclesSection investorId={initial.id} />
          </section>
        )}

        {/* SECTION 5 — Ledger. */}
        {initial?.id && (
          <section data-section="ledger" className="space-y-3 border-t border-border pt-6">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {t('investors.section.ledger')}
            </h3>
            <LedgerView subjectKind="investor" subjectId={initial.id} />
          </section>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6 border-t border-border bg-muted/30 shrink-0">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" variant="gold" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitLabel ?? t('common.save')}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
